import { KEYCLOAK, API_BASE_URL as CONFIG_API_BASE_URL, PIATTAFORMA_MOBILE, VERSIONE } from "@/config";
import diagnosticaUscita, { eVolontaria, percorsoPulito } from "@/services/diagnosticaUscita";
import { erroreDiAccesso, esitoDelRinnovo, gettoniDa } from "@/services/portaAccesso";
import i18n from "@/i18n";
import { vediCome } from "@/services/vediCome";

// Di Keycloak qui servono solo i NOMI: l'emittente dei gettoni (issuerAtteso) e il client a cui
// appartengono, che il backend deve conoscere per rinnovarli. Il browser con Keycloak non parla.
const keycloakConfig = { ...KEYCLOAK };

/**
 * LO STATO DELLA SESSIONE: i gettoni e quello che se ne legge.
 *
 * Prima era l'istanza di keycloak-js, ma dell'adattatore si usavano solo questi campi: accesso,
 * rinnovo e uscita li faceva gia' questo file a mano. Il resto (il controllo silenzioso in un
 * iframe, i redirect verso le pagine di Keycloak) non poteva servire: con il password grant nel
 * browser non nasce mai la sessione di Keycloak che quel controllo cercava. Dal novembre 2026
 * entrare, rinnovare e uscire passano dal backend (PORTA), e Keycloak puo' stare fuori da Internet.
 */
const keycloak = {
  token: null,
  refreshToken: null,
  idToken: null,
  tokenParsed: null,
  refreshTokenParsed: null,
  idTokenParsed: null,
  authenticated: false,
  subject: null,
};

/** La porta d'accesso del backend (AccessoKeycloak): /entra, /rinnova, /esci. */
const PORTA = `${CONFIG_API_BASE_URL}/public/accesso`;

// Token refresh interval (in ms) - refresh when less than 60 seconds left
const MIN_TOKEN_VALIDITY = 60;
const TOKEN_STORAGE_KEY = 'zoelia_tokens';
const COOKIE_RISERVA = 'zt_rt';

// Un rinnovo che non risponde entro questo tempo conta come rete assente. Prima fetch non aveva
// limite: su una rete appesa (radio che si riaggancia, Wi-Fi del portone) tutte le chiamate
// restavano in coda dietro un rinnovo che non tornava mai.
const TEMPO_MASSIMO_RINNOVO_MS = 15000;

// Le attese fra un tentativo di rinnovo e il successivo, quando il token e' gia' scaduto e non lo
// si puo' spedire. Sette secondi in tutto: il tempo tipico perche' la radio si riagganci.
const ATTESE_RIPROVA_RINNOVO_MS = [1000, 2000, 4000];

// Sotto questi secondi di vita residua il token non si spedisce piu': arriverebbe scaduto.
const MARGINE_TOKEN_USABILE = 5;

// Dopo un ingresso si aspetta un poco prima di spedire il resoconto dell'ultima uscita: nel
// frattempo si apre la sessione del secondo fattore, senza la quale il server lo negherebbe.
const ATTESA_INVIO_DIAGNOSTICA_MS = 5000;
// Se il server lo nega comunque perche' il codice del secondo fattore non e' ancora stato dato
// (arriva per posta, e ci vuole un minuto), si riprova ogni tanto per un po'. Dopo il codice
// l'app non si ricarica: senza questi giri il resoconto aspetterebbe l'apertura successiva.
const RIPROVA_DIAGNOSTICA_MS = 60000;
const GIRI_DIAGNOSTICA_MAX = 10;

/**
 * I percorsi che si aprono senza accesso. Una lista sola, usata anche dalla guardia del router
 * (router/index.js): prima erano due copie e questa aveva perso '/oauth/' e '/paga/', quindi chi
 * apriva il link di pagamento con una sessione vecchia poteva finire sul modulo d'accesso.
 */
export const PERCORSI_PUBBLICI = ['/login', '/logout', '/forgot-password', '/auth/', '/access/', '/checkin/', '/oauth/', '/invito/', '/paga/'];

let refreshInterval = null;
let initialized = false;
let initPromise = null;

// UN SOLO RINNOVO ALLA VOLTA.
//
// Alla riapertura dell'app partono insieme messaggi non letti, notifiche, biglietto del canale
// eventi, notifiche del telefono e il timer: con il token scaduto ognuno chiedeva il suo rinnovo,
// e bastava che UNO su cinque fallisse per la rete perche' si uscisse. Chi arriva mentre un
// rinnovo e' in corso aspetta quello; la promessa si azzera appena finito (vedi refreshToken).
let rinnovoInCorso = null;
// Lo stesso per la serie di tentativi con le attese (vedi riprovaRinnovo).
let riprovaInCorso = null;
// Com'e' andato l'ultimo rinnovo: finisce nel resoconto dell'uscita. Mai il token.
let ultimoRinnovo = null;
// Si sta uscendo: un rinnovo che torna adesso non deve rimettere i token appena cancellati, e
// un secondo logout() (un'altra chiamata che prende 401 nello stesso istante) non deve rifare
// revoca e resoconto.
let uscitaAvviata = false;

/**
 * Chi deve fare qualcosa quando si esce, PRIMA che i token spariscano.
 *
 * Un registro e non un import, di proposito: chi ha bisogno di pulire all'uscita (le notifiche
 * push dell'app, che devono togliere il telefono dal server e cancellare il gettone) importa api.js,
 * e api.js importa questo file. Importarlo da qui chiuderebbe il cerchio keycloak -> push -> api ->
 * keycloak. Cosi' invece e' chi ha bisogno che si iscrive, e questo file non sa nemmeno chi e'.
 *
 * Nel sito nessuno si iscrive: logout() fa esattamente quello che faceva prima.
 */
const ganciUscita = [];

/**
 * Iscrive una funzione da chiamare all'uscita. Riceve { token }: il token d'accesso ancora valido,
 * per l'ultima chiamata al server. Deve essere sincrona e veloce: subito dopo la pagina se ne va.
 * Iscrivere due volte la stessa funzione non la fa chiamare due volte.
 * @param {Function} fn
 */
export function alUscita(fn) {
  if (typeof fn === 'function' && !ganciUscita.includes(fn)) {
    ganciUscita.push(fn);
  }
}

/**
 * Save tokens to localStorage + cookie (cookie survives Chrome Android tab kills)
 */
function saveTokens() {
  if (keycloak.token && keycloak.refreshToken) {
    const tokens = {
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
      idToken: keycloak.idToken,
    };
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    // Nell'app il cookie di riserva non serve e non funzionerebbe: e' pensato per Chrome su
    // Android, che a volte svuota il localStorage di una scheda, mentre il localStorage della
    // WebView e' privato dell'app e non lo tocca nessuno.
    if (PIATTAFORMA_MOBILE) return;
    // Il cookie e' legato al SOLO host, senza domain=. Prima stava sul dominio padre (.zoelia.io),
    // e app.zoelia.io e staging.zoelia.io ne avevano uno solo, che si sovrascrivevano a vicenda:
    // con il localStorage vuoto si provava il token dell'altro ambiente, Keycloak rispondeva
    // "Invalid refresh token" e si finiva al login. In piu' il refresh token di produzione
    // viaggiava verso ogni host *.zoelia.io, staging compreso.
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${COOKIE_RISERVA}=${encodeURIComponent(tokens.refreshToken)}; expires=${expires}; path=/; SameSite=Lax; Secure`;
    // A ogni salvataggio, non solo all'uscita: una sessione nata con le versioni precedenti
    // continua a rinnovarsi per settimane senza mai uscire, e intanto il suo refresh token restava
    // nel cookie del dominio padre, spedito a staging e a ogni *.zoelia.io. Costa una riga.
    cancellaCookieDiDominio();
  }
}

/**
 * Fa scadere il vecchio cookie di riserva sul dominio padre (.zoelia.io), quello scritto dalle
 * versioni precedenti. Puo' essere quello dell'altro ambiente: e' la stessa cosa che succedeva
 * prima, quando i due se lo sovrascrivevano, e l'altro ambiente ha ormai il suo, legato all'host.
 */
function cancellaCookieDiDominio() {
  const domain = window.location.hostname.split('.').slice(-2).join('.');
  document.cookie = `${COOKIE_RISERVA}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; Secure; domain=.${domain}`;
}

/**
 * L'issuer dei token di QUESTO ambiente, come lo scrive Keycloak nel campo iss.
 * Produzione e staging stanno sullo stesso Keycloak con realm diversi (zoelia, zoelia-staging).
 */
function issuerAtteso() {
  return `${String(keycloakConfig.url || '').replace(/\/+$/, '')}/realms/${keycloakConfig.realm}`;
}

/**
 * Il refresh token del cookie di riserva, se ce n'e' uno di questo ambiente e non e' scaduto.
 *
 * Si guardano TUTTI i cookie con quel nome: nel passaggio convivono il vecchio, sul dominio padre
 * e forse dell'altro ambiente, e il nuovo, legato all'host; document.cookie li elenca entrambi.
 * Il campo iss si legge senza verificare la firma: qui non si decide nulla sulla fiducia (quello
 * lo fa Keycloak al rinnovo), si evita solo di mandargli un token che rifiuterebbe comunque.
 * @returns {string|null}
 */
function refreshTokenDalCookie() {
  const adesso = Math.floor(Date.now() / 1000);
  const atteso = issuerAtteso();
  const prefisso = `${COOKIE_RISERVA}=`;
  for (const pezzo of document.cookie.split(/;\s*/)) {
    if (!pezzo.startsWith(prefisso)) continue;
    let valore;
    try {
      valore = decodeURIComponent(pezzo.slice(prefisso.length));
    } catch (e) {
      continue;
    }
    if (!valore || valore.length <= 10) continue;
    const letto = parseJwt(valore);
    if (!letto || letto.iss !== atteso) {
      console.warn('Keycloak: cookie di riserva di un altro ambiente, ignorato');
      continue;
    }
    if (letto.exp && letto.exp < adesso) continue;
    return valore;
  }
  return null;
}

/**
 * Load tokens from localStorage (primary) or cookie (fallback) and restore Keycloak state
 * @returns {boolean} - true if tokens were restored
 */
function loadTokens() {
  try {
    let stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    // Fallback: read refresh token from cookie if localStorage was wiped (Chrome Android)
    if (!stored && !PIATTAFORMA_MOBILE) {
      const refreshToken = refreshTokenDalCookie();
      if (refreshToken) {
        console.log('Keycloak: Refresh token restored from cookie backup');
        // Build minimal token state — refresh will get a new access token
        keycloak.refreshToken = refreshToken;
        keycloak.refreshTokenParsed = parseJwt(refreshToken);
        keycloak.authenticated = false; // Lo rimette a vero initKeycloak, dopo il rinnovo
        return true; // Signal that we have a refresh token to try
      }
    }
    if (!stored) return false;

    const tokens = JSON.parse(stored);
    if (!tokens.token || !tokens.refreshToken) return false;

    // Check if refresh token is expired
    const refreshTokenParsed = parseJwt(tokens.refreshToken);
    if (refreshTokenParsed && refreshTokenParsed.exp) {
      const now = Math.floor(Date.now() / 1000);
      if (refreshTokenParsed.exp < now) {
        // Refresh token expired, clear storage. Si annota: e' l'uscita silenziosa del sito
        // senza «ricordami», che altrimenti si vede solo come una pagina di login. I token
        // letti si passano a mano: in memoria non ci sono ancora (e non ci devono andare), e
        // senza il resoconto non saprebbe ne' la scadenza ne' di chi era la sessione.
        registraUscita('avvio_sessione_scaduta', {
          accesso: parseJwt(tokens.token),
          rinnovo: refreshTokenParsed,
        });
        clearTokens();
        return false;
      }
    }

    // Restore tokens to Keycloak instance
    keycloak.token = tokens.token;
    keycloak.refreshToken = tokens.refreshToken;
    keycloak.idToken = tokens.idToken;
    keycloak.tokenParsed = parseJwt(tokens.token);
    keycloak.refreshTokenParsed = refreshTokenParsed;
    keycloak.idTokenParsed = tokens.idToken ? parseJwt(tokens.idToken) : null;
    keycloak.authenticated = true;
    keycloak.subject = keycloak.tokenParsed?.sub;

    return true;
  } catch (error) {
    console.error('Error loading tokens:', error);
    clearTokens();
    return false;
  }
}

/**
 * Clear tokens from localStorage and cookie
 */
function clearTokens() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  // Nell'app il cookie di riserva non c'e' mai stato (vedi saveTokens).
  if (PIATTAFORMA_MOBILE) return;
  // Quello di adesso, legato all'host.
  document.cookie = `${COOKIE_RISERVA}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; Secure`;
  // E quello vecchio sul dominio padre, finche' ce n'e' in giro qualcuno scritto dalle versioni
  // precedenti: altrimenti resterebbe li' trenta giorni a mandare il refresh token a ogni
  // *.zoelia.io.
  cancellaCookieDiDominio();
}

/** La sessione Keycloak a cui appartiene un token letto (sid; session_state nei token vecchi). */
function sessioneDi(letto) {
  return letto?.sid || letto?.session_state || null;
}

/** La sessione dei token che questa pagina ha in mano. */
function sessioneMia() {
  return sessioneDi(keycloak.refreshTokenParsed) || sessioneDi(keycloak.tokenParsed);
}

/**
 * La sessione dei token salvati in un testo dell'archivio (il valore di TOKEN_STORAGE_KEY), o
 * null se non si sa dire.
 */
function sessioneSalvataIn(testo) {
  try {
    const salvati = testo ? JSON.parse(testo) : null;
    return salvati?.refreshToken ? sessioneDi(parseJwt(salvati.refreshToken)) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Nell'archivio ci sono i token di un'ALTRA sessione? Succede nel sito con due schede: una rimasta
 * indietro con la sessione vecchia, ormai finita, e una in cui si e' rientrati. Se non si sa dire
 * (token senza sid, archivio illeggibile) si risponde no, cioe' si fa come prima.
 */
function archivioDiUnAltraSessione() {
  try {
    const loro = sessioneSalvataIn(localStorage.getItem(TOKEN_STORAGE_KEY));
    const mia = sessioneMia();
    return !!(loro && mia && loro !== mia);
  } catch (e) {
    return false;
  }
}

/**
 * SITO, PIU' SCHEDE: un'altra scheda ha tolto i token della nostra stessa sessione.
 *
 * Le schede condividono il localStorage ma non si parlavano. Dopo «Esci» in una scheda, le altre
 * avevano ancora i token in memoria e al primo rinnovo li riscrivevano, cookie compreso: su un
 * computer condiviso chi riapriva la pagina ritrovava la sessione di chi era appena uscito. E se
 * nella prima scheda era entrata un'altra persona (l'invito), la seconda le sovrascriveva i token.
 *
 * L'evento 'storage' arriva solo dalle ALTRE schede (mai da questa, mai da Chrome che svuota
 * l'archivio da se'). Si esce solo se i token tolti erano della nostra sessione: una scheda rimasta
 * indietro con una sessione vecchia non deve far uscire quella buona (vedi logout, che in quel caso
 * non li tocca nemmeno). localStorage.clear() lo fa solo la rotta /logout, cioe' «Esci».
 *
 * Niente revoca e niente resoconto: li ha gia' fatti la scheda da cui si e' usciti.
 */
function alCambioInUnAltraScheda(evento) {
  try {
    if (evento.key !== TOKEN_STORAGE_KEY && evento.key !== null) return;
    if (!keycloak.authenticated || uscitaAvviata) return;
    if (haSessioneSalvata()) return;
    if (evento.key === TOKEN_STORAGE_KEY) {
      const loro = sessioneSalvataIn(evento.oldValue);
      const mia = sessioneMia();
      if (loro && mia && loro !== mia) return;
    }
    uscitaAvviata = true;
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
    keycloak.token = null;
    keycloak.refreshToken = null;
    keycloak.idToken = null;
    keycloak.tokenParsed = null;
    keycloak.authenticated = false;
    // sessionStorage e' di questa scheda: l'«Esci» dell'altra non l'ha toccato. Chi entra dopo non deve ritrovarsi il "vedi come".
    vediCome.pulisci();
    if (!isOnPublicRoute()) {
      window.location.href = window.location.origin + '/login';
    }
  } catch (e) {
    // e' coordinamento fra schede: se non riesce, ci pensa il 401 della prossima chiamata
  }
}

// Nell'app c'e' una sola pagina: nessun'altra scheda da ascoltare.
if (!PIATTAFORMA_MOBILE && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', alCambioInUnAltraScheda);
}

/**
 * C'e' ancora una sessione salvata su questo dispositivo?
 *
 * Serve a distinguere i due motivi per cui initKeycloak() puo' rispondere false, che per le
 * notifiche push dell'app vogliono dire cose opposte:
 * - la sessione e' FINITA (Keycloak ha rifiutato il rinnovo, il refresh token e' scaduto, i token
 *   salvati erano illeggibili): in tutti questi percorsi si passa da clearTokens(), e qui non c'e'
 *   piu' niente. Il telefono va tolto dalle notifiche.
 * - il rinnovo NON SI E' POTUTO CHIEDERE (niente rete, Keycloak in errore 5xx) con il token
 *   d'accesso gia' scaduto: i token restano salvati apposta, perche' sono probabilmente ancora
 *   buoni. Cancellare qui il gettone FCM e svuotare la tendina vorrebbe dire lasciare senza push,
 *   fino alla prossima apertura riuscita, chi ha solo aperto l'app in galleria.
 *
 * Si guarda l'archivio e non il token in memoria: clearTokens() svuota localStorage e cookie ma
 * lascia keycloak.refreshToken com'era, quindi in memoria le due situazioni si somigliano.
 * Archivio negato: nessuna sessione, come loadTokens().
 * @returns {boolean}
 */
export function haSessioneSalvata() {
  try {
    return !!localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    return false;
  }
}

/**
 * Initialize Keycloak without forcing login
 * @returns {Promise<boolean>} - true if already authenticated
 */
export async function initKeycloak() {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    // Il rinnovo all'avvio non si e' potuto fare per la rete e non c'e' un token con cui entrare.
    let senzaReteAllAvvio = false;
    try {
      // First, try to restore tokens from localStorage
      const tokensRestored = loadTokens();
      if (tokensRestored) {
        console.log('Keycloak: Tokens restored from storage');
        // Verify tokens are still valid by refreshing
        let esito = await refreshToken();
        // Rete assente all'avvio: gli stessi tentativi di ensureValidToken (1, 2 e 4 s), perche'
        // l'avvio a freddo e' proprio il momento in cui la radio si sta riagganciando, e senza
        // si finiva sulla pagina d'accesso con la sessione buona. Non dopo un rinnovo rimasto
        // appeso fino al tempo massimo: con il logo gia' andato via, altri tre tentativi da 15 s
        // sarebbero un minuto di schermo vuoto.
        if (esito === 'rete' && ultimoRinnovo?.dettaglio !== 'tempo scaduto') {
          esito = await riprovaRinnovo();
        }
        if (esito === 'ok') {
          console.log('Keycloak: Session restored successfully');
          // Nel ramo del cookie di riserva loadTokens() lascia authenticated a falso e non ha
          // un token d'accesso da cui leggere chi e' la persona: senza queste due righe
          // initKeycloak rispondeva "entrato" ma la guardia del router vedeva "non autenticato"
          // e mandava al login, rendendo inutile proprio la riserva pensata per Chrome Android.
          keycloak.authenticated = true;
          keycloak.subject = keycloak.tokenParsed?.sub;
          startTokenRefresh();
          initialized = true;
          programmaInvioDiagnostica();
          return true;
        }
        if (esito === 'rete') {
          // Non si e' potuto chiedere. I token NON si buttano: sono probabilmente ancora buoni,
          // e cancellarli qui vorrebbe dire trasformare un buco di rete di due secondi in un
          // logout definitivo — il refresh token durava trenta giorni e l'abbiamo perso.
          console.warn('Keycloak: rete assente al ripristino, tengo i token e riprovo dopo');
          if (accessoAncoraValido()) {
            keycloak.authenticated = true;
            startTokenRefresh();
            initialized = true;
            programmaInvioDiagnostica();
            return true;
          }
          // Si finisce sulla pagina d'accesso, con i token ancora salvati: per la persona e' un
          // "mi ha scollegato" come gli altri, e va contato come gli altri.
          senzaReteAllAvvio = true;
        } else {
          console.warn('Keycloak: Stored token refresh failed (possibly invalidated by browser kill)');
          // Non e' un logout (nessuna revoca, nessun gancio), ma e' un'uscita: si annota.
          registraUscita('avvio_rinnovo_rifiutato');
          clearTokens();
          keycloak.authenticated = false;
        }
      }

      // NESSUNA SESSIONE DA RIPRENDERE: CI PENSA IL MODULO D'ACCESSO.
      //
      // Qui prima, nel sito, partiva keycloak-js: un iframe verso auth.zoelia.io per il controllo
      // silenzioso e, se falliva con dei token salvati, keycloak.login({ prompt: 'none' }), cioe' un
      // redirect. Non poteva trovare niente, perche' il password grant non lascia nel browser la
      // sessione di Keycloak che quel controllo cercava; e su Google auth.zoelia.io da fuori non
      // risponde, quindi il redirect porterebbe la persona su una pagina d'errore. Sito e app fanno
      // ora la stessa cosa, quella che l'app faceva gia' (in una WebView quel redirect l'avrebbe
      // portata fuori dall'app).
      if (senzaReteAllAvvio) registraUscita('avvio_senza_rete');
      keycloak.authenticated = false;
      initialized = true;
      return false;
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      initialized = true;
      return false;
    }
  })();

  return initPromise;
}

/**
 * Check if Keycloak has been initialized
 * @returns {boolean}
 */
export function isInitialized() {
  return initialized;
}

/**
 * Wait for Keycloak to be initialized
 * @returns {Promise<boolean>}
 */
export async function waitForInit() {
  if (initialized) {
    return keycloak.authenticated || false;
  }
  if (initPromise) {
    return initPromise;
  }
  // If not initialized and no promise, init now
  return initKeycloak();
}

/**
 * Accesso con nome e password: il password grant di Keycloak, chiesto dal backend
 * (`/public/accesso/entra`), con lo stesso client e lo stesso scope che prima si mandavano da qui.
 *
 * Il quarto parametro e' il codice dell'app di autenticazione, e non e' opzionale per chi ce
 * l'ha: nel realm il sotto-flusso `Direct Grant - Conditional OTP` e' ACCESO, quindi appena una
 * persona configura il TOTP questa richiesta, senza codice, viene rifiutata. Keycloak risponde
 * `invalid_grant / Invalid user credentials` sia per la password sbagliata, sia per il codice
 * mancante, sia per il codice sbagliato; il backend dice CODICE_RICHIESTO solo nel secondo caso,
 * guardando DOPO il rifiuto.
 *
 * Per questo il codice NON si tenta al buio. Ogni tentativo fallito conta per la protezione
 * forza bruta del realm (`failureFactor: 5`, blocco da 60 s fino a 15 minuti), che conta per
 * account e funziona anche ora che chiede il backend: provare senza codice per capire se serve
 * chiuderebbe fuori, in cinque accessi normali, chi non ha sbagliato niente. Si chiede prima al
 * backend quale fattore ha quell'indirizzo (`/public/accesso/fattore`).
 *
 * @param {string} username
 * @param {string} password
 * @param {boolean} rememberMe - con true chiede offline_access, sessione lunga
 * @param {string|null} codiceTotp - il codice dell'app, per chi usa quel fattore
 * @returns {Promise<{success: boolean, error?: string, codice?: string|null}>} codice: quello
 *   d'errore del backend (CODICE_RICHIESTO, ACCOUNT_BLOCCATO...), error: gia' tradotto
 */
export async function login(username, password, rememberMe = false, codiceTotp = null) {
  // Nell'app la sessione e' sempre quella lunga. La normale del realm dura al massimo 10 ore,
  // e sul telefono vorrebbe dire rifare l'accesso quasi ogni volta che si riapre l'app; quella
  // offline dura 30 giorni di inattivita'. E' la stessa scelta che nel sito fa la casella
  // «ricordami», che nell'app non si mostra. Lo scope lo compone il backend da questo.
  const ricordami = !!(rememberMe || PIATTAFORMA_MOBILE);

  try {
    // fetch e non apiClient: senza intestazione Authorization (un gettone scaduto su una rotta
    // pubblica lo rifiuterebbe il server) e senza l'interceptor, che per un 401 rinnoverebbe.
    const response = await fetch(`${PORTA}/entra`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        ricordami,
        clientId: keycloakConfig.clientId,
        // Solo se c'e': mandare un codice vuoto a chi non ha il fattore configurato non cambia
        // niente, ma mandarlo a chi ce l'ha con un valore sbagliato brucia un tentativo.
        ...(codiceTotp ? { codice: String(codiceTotp).replace(/\D/g, '') } : {}),
      }),
    });

    const corpo = await response.json().catch(() => null);
    const tokens = response.ok ? gettoniDa(corpo) : null;
    if (!tokens) {
      const { codice, chiave } = erroreDiAccesso(response.status, corpo);
      return { success: false, codice, error: i18n.global.t(chiave) };
    }

    keycloak.token = tokens.accessToken;
    keycloak.refreshToken = tokens.refreshToken;
    keycloak.idToken = tokens.idToken || null;
    keycloak.tokenParsed = parseJwt(tokens.accessToken);
    keycloak.refreshTokenParsed = parseJwt(tokens.refreshToken);
    keycloak.idTokenParsed = tokens.idToken ? parseJwt(tokens.idToken) : null;
    keycloak.authenticated = true;
    keycloak.subject = keycloak.tokenParsed?.sub;
    uscitaAvviata = false;
    // Chi entra con la password e' se stesso, qualunque cosa si ricordasse la scheda.
    vediCome.pulisci();

    // Save tokens to localStorage for session persistence
    saveTokens();
    startTokenRefresh();
    programmaInvioDiagnostica();

    console.log('Keycloak: Login successful');
    return { success: true };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, codice: null, error: i18n.global.t('t-login-error') };
  }
}

/**
 * Parse JWT token
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

/**
 * Il token d'accesso che abbiamo in mano e' ancora buono per un po'?
 *
 * Serve nel solo caso in cui non si e' potuto chiedere niente a Keycloak perche' non c'era
 * rete: se il token in tasca vale ancora, si lavora con quello invece di mandare l'utente al
 * login per un'assenza di segnale.
 */
function accessoAncoraValido() {
  const scadenza = keycloak.tokenParsed?.exp;
  if (!scadenza) return false;
  return scadenza - Math.floor(Date.now() / 1000) > MIN_TOKEN_VALIDITY;
}

/**
 * Il token d'accesso si puo' ancora spedire? Piu' permissivo di accessoAncoraValido(): qui basta
 * che arrivi al server prima di scadere. Serve a decidere se, senza rete per rinnovarlo, lo si
 * manda lo stesso (si') o se la chiamata deve fallire per rete (no: il server risponderebbe 401).
 */
function tokenUsabile() {
  const scadenza = keycloak.tokenParsed?.exp;
  if (!scadenza) return false;
  return scadenza - Math.floor(Date.now() / 1000) > MARGINE_TOKEN_USABILE;
}

function attendi(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Annota perche' si esce (vedi diagnosticaUscita). Va chiamata PRIMA di azzerare i token: la
 * scadenza del token d'accesso e' uno dei dati che contano.
 * @param {string} motivo
 * @param {{percorso?: string|null, accesso?: Object|null, rinnovo?: Object|null}} dettagli
 *   percorso: la chiamata che ha fatto uscire; accesso e rinnovo: i token GIA' LETTI da usare al
 *   posto di quelli in memoria, quando in memoria non ci sono ancora (l'avvio).
 */
function registraUscita(motivo, { percorso = null, accesso = null, rinnovo = null } = {}) {
  try {
    const adesso = Math.floor(Date.now() / 1000);
    const letto = accesso || keycloak.tokenParsed;
    const scadenza = letto?.exp;
    diagnosticaUscita.registra({
      ora: new Date().toISOString(),
      motivo,
      // Di chi era la sessione: il resoconto si spedisce solo se rientra la stessa persona. Su un
      // dispositivo condiviso, altrimenti, l'uscita di una finirebbe nel log a nome dell'altra.
      // E' l'identificativo Keycloak, non un token; al server non si manda (vedi invia).
      sub: letto?.sub || (rinnovo || keycloak.refreshTokenParsed)?.sub || null,
      percorso: percorsoPulito(percorso),
      pagina: percorsoPulito(window.location.pathname),
      // Positivo: il token era scaduto da tanti secondi. Negativo: valeva ancora.
      secondiDallaScadenza: scadenza ? adesso - scadenza : null,
      esitoUltimoRinnovo: ultimoRinnovo?.esito || null,
      dettaglioUltimoRinnovo: ultimoRinnovo?.dettaglio || null,
      secondiDaUltimoRinnovo: ultimoRinnovo ? adesso - ultimoRinnovo.ora : null,
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      piattaforma: PIATTAFORMA_MOBILE ? 'app' : 'web',
      versione: VERSIONE,
    });
  } catch (e) {
    // la misura non deve mai impedire di uscire
  }
}

/**
 * Se c'e' il resoconto di un'uscita precedente, lo spedisce fra poco (vedi
 * ATTESA_INVIO_DIAGNOSTICA_MS). Si usa il token che c'e' e non ensureValidToken(): una chiamata di
 * misura non deve poter far partire un rinnovo, e tanto meno un'uscita. Se il token nel frattempo
 * non va, il resoconto aspetta il prossimo ingresso.
 */
function programmaInvioDiagnostica(giro = 1) {
  if (!diagnosticaUscita.presente()) return;
  setTimeout(() => {
    if (uscitaAvviata || !keycloak.authenticated || !tokenUsabile()) return;
    diagnosticaUscita.invia(keycloak.token, keycloak.tokenParsed?.sub)
      .then((esito) => {
        // Negato per il secondo fattore: il codice arriva fra poco, e dopo non si ricarica.
        if (esito === 'secondo_fattore' && giro < GIRI_DIAGNOSTICA_MAX) {
          setTimeout(() => programmaInvioDiagnostica(giro + 1), RIPROVA_DIAGNOSTICA_MS - ATTESA_INVIO_DIAGNOSTICA_MS);
        }
      })
      .catch(() => { /* e' misura */ });
  }, ATTESA_INVIO_DIAGNOSTICA_MS);
}

/**
 * Check if current page is a public route (no auth required)
 */
function isOnPublicRoute() {
  // '/invito/' c'e' per una ragione poco ovvia: la pagina dell'invito chiama il server, e
  // l'interceptor passa da ensureValidToken(), che su una rotta NON pubblica fa logout e
  // sbatte su /login. Chi arriva sul link con una sessione vecchia perderebbe l'invito. Lo
  // stesso per '/paga/': chi paga non deve mai finire sul modulo d'accesso.
  return PERCORSI_PUBBLICI.some(path => window.location.pathname.startsWith(path));
}

/**
 * Start automatic token refresh — only refresh when token is about to expire.
 * With offline_access, the refresh token lasts 30 days, so no need for aggressive polling.
 */
function startTokenRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  // Check every 60 seconds, but only refresh if token expires in < 90 seconds
  refreshInterval = setInterval(async () => {
    if (!keycloak.authenticated) return;

    const tokenExp = keycloak.tokenParsed?.exp;
    const now = Math.floor(Date.now() / 1000);
    const timeLeft = tokenExp ? tokenExp - now : 999;

    // Only refresh if less than 90 seconds left
    if (timeLeft > 90) return;

    try {
      const esito = await refreshToken();
      if (esito === 'ok') {
        console.debug('Token refreshed');
      } else if (esito === 'rete') {
        // Si riprova al giro dopo. Nessun logout: il telefono che si riaggancia alla rete non
        // e' una sessione scaduta, ed e' esattamente il momento in cui si riapre il browser.
        console.warn('Rinnovo del token: rete assente, riprovo fra un minuto');
      } else {
        // Only logout/redirect if user is on a protected route
        if (!isOnPublicRoute()) {
          logout('rinnovo_rifiutato');
        } else {
          // On public pages, just stop refreshing silently
          clearInterval(refreshInterval);
          refreshInterval = null;
        }
      }
    } catch (error) {
      // refreshToken non lancia piu': se si arriva qui e' un guaio nostro, non della sessione.
      console.error('Token refresh failed:', error);
    }
  }, 60000); // Check every 60 seconds (refresh only when < 90s left)
}

/**
 * Rinnova il token d'accesso, e dice PERCHE' se non ci riesce.
 *
 * Prima tornava un booleano, e "false" voleva dire due cose molto diverse: "Keycloak dice che
 * questa sessione non vale piu'" e "non c'e' rete". Chi chiamava le trattava tutte e due come
 * la prima, e faceva logout. Sul telefono e' il caso normale: si riapre il browser dopo un po',
 * il token e' scaduto, la radio si sta ancora riagganciando, la prima chiamata non parte — e
 * l'utente si ritrovava fuori per due secondi di rete mancante.
 *
 * Un solo rinnovo alla volta: chi chiama mentre ce n'e' uno in corso riceve la stessa promessa
 * (vedi rinnovoInCorso). La promessa si azzera appena il rinnovo finisce, qualunque sia l'esito,
 * quindi chi chiama dopo ne fa partire uno nuovo.
 *
 * @returns {Promise<'ok'|'rete'|'finita'>}
 *   'ok'     rinnovato;
 *   'rete'   Keycloak non ha dato un verdetto sulla sessione (rete assente, tempo scaduto, 5xx,
 *            CORS, risposta illeggibile, un 4xx che non e' invalid_grant): la sessione
 *            probabilmente c'e' ancora, si riprova piu' tardi e NON si butta fuori nessuno;
 *   'finita' Keycloak ha risposto invalid_grant: la sessione e' davvero conclusa.
 */
function refreshToken() {
  if (!rinnovoInCorso) {
    rinnovoInCorso = eseguiRinnovo().finally(() => {
      rinnovoInCorso = null;
    });
  }
  return rinnovoInCorso;
}

/** Il rinnovo vero e proprio. Non lancia mai: ogni guaio diventa un esito. */
async function eseguiRinnovo() {
  const usato = keycloak.refreshToken;
  let { esito, dettaglio, ripetibile = true } = await chiediRinnovo();
  // Mentre si aspettava la risposta e' entrato qualcuno (login(): la pagina dell'invito, il
  // modulo d'accesso): il rifiuto, o il guasto, riguarda i token di prima, non quelli nuovi.
  // Rispondere 'finita' faceva uscire chi era appena entrato; i token in mano sono freschi.
  if (esito !== 'ok' && !uscitaAvviata && keycloak.refreshToken && keycloak.refreshToken !== usato) {
    esito = 'ok';
    dettaglio = 'token gia\' cambiato';
    ripetibile = true;
  }
  ultimoRinnovo = { esito, dettaglio, ripetibile, ora: Math.floor(Date.now() / 1000) };
  if (esito !== 'ok') {
    // Il dettaglio e' la descrizione di Keycloak ("Session not active") o la natura del guasto:
    // mai il token.
    console.warn(`Keycloak: rinnovo non riuscito (${esito}: ${dettaglio})`);
  }
  return esito;
}

async function chiediRinnovo() {
  const usato = keycloak.refreshToken;
  if (!usato) return { esito: 'finita', dettaglio: 'nessun refresh token' };

  const controllo = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let tempoScaduto = false;
  const timer = setTimeout(() => {
    tempoScaduto = true;
    if (controllo) controllo.abort();
  }, TEMPO_MASSIMO_RINNOVO_MS);

  try {
    // Il rinnovo lo chiede il backend a Keycloak, a nome dello stesso client: i refresh token nati
    // prima di questa porta si rinnovano qui come prima. Il refresh token viaggia nel corpo, mai
    // nell'indirizzo, che finisce nei log dei proxy.
    const response = await fetch(`${PORTA}/rinnova`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ refreshToken: usato, clientId: keycloakConfig.clientId }),
      signal: controllo ? controllo.signal : undefined,
    });

    // Solo SESSIONE_FINITA (Keycloak ha detto invalid_grant: token scaduto, sessione non attiva,
    // sessione offline sparita) fa uscire. Un 5xx, un errore di configurazione del client, una
    // risposta che non e' nostra non dicono niente sulla sessione della persona, e buttarla fuori
    // non li aggiusterebbe: le regole stanno in esitoDelRinnovo (portaAccesso.js).
    const corpo = await response.json().catch(() => null);
    const letto = esitoDelRinnovo(response.status, corpo);
    if (letto.esito !== 'ok') {
      return { esito: letto.esito, dettaglio: letto.dettaglio, ripetibile: letto.ripetibile };
    }
    const tokens = letto.gettoni;

    // Mentre si aspettava la risposta si e' usciti, o e' entrato qualcuno: i token in memoria non
    // sono piu' quelli da cui si e' partiti, e rimetterli ora riporterebbe dentro chi ha appena
    // premuto «Esci» (saveTokens li riscriverebbe nel localStorage).
    if (uscitaAvviata) return { esito: 'finita', dettaglio: 'uscita in corso' };
    if (keycloak.refreshToken !== usato) return { esito: 'ok', dettaglio: 'token gia\' cambiato' };

    keycloak.token = tokens.accessToken;
    keycloak.refreshToken = tokens.refreshToken;
    keycloak.tokenParsed = parseJwt(tokens.accessToken);
    keycloak.refreshTokenParsed = parseJwt(tokens.refreshToken);

    // Update stored tokens
    saveTokens();

    return { esito: 'ok', dettaglio: null };
  } catch (error) {
    // fetch lancia quando la richiesta non e' partita o non e' tornata: rete, CORS (per esempio
    // un errore del proxy, che non ha le intestazioni del backend), tempo scaduto. E' la rete,
    // non la sessione.
    return { esito: 'rete', dettaglio: tempoScaduto ? 'tempo scaduto' : 'rete' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Riprova il rinnovo dopo 1, 2 e 4 secondi, finche' la rete non da' una risposta. Da usare
 * DOPO un primo tentativo finito in 'rete', quando senza un token nuovo non si puo' andare avanti.
 *
 * Condivisa come refreshToken: cinque chiamate ferme sullo stesso token scaduto aspettano la
 * stessa serie di tentativi, invece di farne partire cinque.
 * @returns {Promise<'ok'|'rete'|'finita'>}
 */
function riprovaRinnovo() {
  // Keycloak ha gia' risposto, e con un errore che ripetuto non cambia (vedi chiediRinnovo).
  if (ultimoRinnovo?.esito === 'rete' && ultimoRinnovo.ripetibile === false) {
    return Promise.resolve('rete');
  }
  if (!riprovaInCorso) {
    riprovaInCorso = (async () => {
      let esito = 'rete';
      for (const ms of ATTESE_RIPROVA_RINNOVO_MS) {
        await attendi(ms);
        if (uscitaAvviata) return 'finita';
        esito = await refreshToken();
        if (esito !== 'rete' || ultimoRinnovo?.ripetibile === false) break;
      }
      return esito;
    })().finally(() => {
      riprovaInCorso = null;
    });
  }
  return riprovaInCorso;
}

/**
 * L'errore che ensureValidToken lancia quando il token e' scaduto e non si e' riusciti a
 * rinnovarlo per la rete: la chiamata deve fallire come errore di rete, non partire con un token
 * che il server rifiuterebbe (e il cui 401, prima, faceva uscire).
 */
function erroreRinnovoSenzaRete() {
  const errore = new Error('Rinnovo del token non riuscito: rete assente');
  errore.rinnovoSenzaRete = true;
  return errore;
}

/**
 * L'errore arriva da un rinnovo mancato per la rete? Per api.js, che lo trasforma in un errore
 * di rete di axios.
 */
export function eRinnovoSenzaRete(errore) {
  return !!(errore && errore.rinnovoSenzaRete);
}

/**
 * Keycloak ha rifiutato la sessione (o il server continua a non riconoscere un token appena
 * rinnovato): si esce, ma non da una pagina pubblica, dove chi guarda puo' restare anche senza
 * sessione (un invito, un check-in).
 * @param {string} motivo
 * @param {{percorso?: string|null}} dettagli
 */
export function sessioneFinita(motivo, dettagli = {}) {
  if (isOnPublicRoute()) return;
  logout(motivo, dettagli);
}

/**
 * Il server ha risposto 401 a una chiamata fatta con tokenUsato. Procura un token con cui
 * ripeterla, e dice com'e' andata (stessi esiti di refreshToken).
 *
 * Se nel frattempo un'altra chiamata ha gia' rinnovato, il token in mano e' diverso da quello
 * rifiutato e basta ripetere. Altrimenti si rinnova anche se il token sembra ancora buono: il
 * server l'ha rifiutato, e per esempio l'orologio del telefono puo' essere indietro. Se non c'e'
 * rete si riprova come in ensureValidToken.
 * @param {string|null} tokenUsato
 * @returns {Promise<'ok'|'rete'|'finita'>}
 */
export async function rinnovaDopo401(tokenUsato) {
  if (uscitaAvviata) return 'finita';
  if (keycloak.token && tokenUsato && keycloak.token !== tokenUsato && tokenUsabile()) {
    return 'ok';
  }
  const esito = await refreshToken();
  return esito === 'rete' ? riprovaRinnovo() : esito;
}

/**
 * Get current access token
 * @returns {string|null}
 */
export function getToken() {
  return keycloak.token || null;
}

/**
 * Get current user profile from token
 * @returns {Object|null}
 */
export function getUserProfile() {
  if (!keycloak.authenticated || !keycloak.tokenParsed) return null;

  // "Vedi come": la barra in alto e le schermate devono mostrare la persona guardata, non chi guarda.
  const guardato = vediCome.stato();
  if (guardato) {
    const [nome, ...resto] = String(guardato.nome || "").split(" ");
    return {
      id: guardato.persona,
      email: guardato.email,
      firstName: nome,
      lastName: resto.join(" "),
      fullName: guardato.nome,
      username: guardato.email,
      locale: keycloak.tokenParsed.locale || 'it',
      roles: [],
    };
  }

  return {
    id: keycloak.subject,
    email: keycloak.tokenParsed.email,
    firstName: keycloak.tokenParsed.given_name,
    lastName: keycloak.tokenParsed.family_name,
    fullName: keycloak.tokenParsed.name,
    username: keycloak.tokenParsed.preferred_username,
    locale: keycloak.tokenParsed.locale || 'it',
    roles: keycloak.tokenParsed.realm_access?.roles || [],
  };
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated() {
  return keycloak.authenticated || false;
}

/**
 * Check if user has a specific role
 * @param {string} role
 * @returns {boolean}
 */
export function hasRole(role) {
  if (vediCome.attivo()) return false;
  if (!keycloak.tokenParsed?.realm_access?.roles) return false;
  return keycloak.tokenParsed.realm_access.roles.includes(role);
}

/**
 * Esce: cancella i token e porta al login (o a redirectUri).
 *
 * @param {string} motivo perche' si esce. Le uscite chieste dalla persona sono 'esci', 'codice'
 *   (dalla schermata del secondo fattore), 'invito', 'dispositivi_revocati', 'password'; quelle
 *   decise dal codice sono 'rinnovo_rifiutato' (Keycloak ha risposto invalid_grant) e
 *   '401_dopo_rinnovo' (il server rifiuta anche un token appena rinnovato). Finisce nel resoconto
 *   dell'uscita (diagnosticaUscita); nell'app decide la revoca, nel sito se toccare i token
 *   salvati da un'altra scheda, vedi sotto.
 * @param {{redirectUri?: string, percorso?: string|null}} opzioni
 *   redirectUri: dove andare dopo (predefinito /login); percorso: la chiamata che ha fatto uscire.
 */
export function logout(motivo = 'esci', { redirectUri, percorso = null } = {}) {
  // La firma di prima era logout(redirectUri): un indirizzo passato al posto del motivo e' un
  // chiamante rimasto indietro, e va capito, non trasformato in un motivo senza senso.
  if (typeof motivo === 'string' && /^(https?:)?\/\//i.test(motivo)) {
    redirectUri = motivo;
    motivo = 'esci';
  }
  if (typeof motivo !== 'string' || !motivo) motivo = 'esci';
  const destinazione = redirectUri || window.location.origin + '/login';

  // Una sola uscita per pagina: dopo c'e' comunque la navigazione. Serve quando piu' chiamate
  // prendono 401 nello stesso istante.
  if (uscitaAvviata) return;
  uscitaAvviata = true;

  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }

  // Prima di azzerare i token: il resoconto vuole sapere quanto era scaduto quello d'accesso.
  registraUscita(motivo, { percorso });

  // Prima di tutto chi si e' iscritto (vedi alUscita): prima della revoca, perche' non ne dipende
  // e un suo guaio non deve toccarla, e prima di azzerare i token, perche' l'ultima chiamata al
  // server ha bisogno di quello d'accesso. Ogni gancio nel suo try: uscire conta piu' che pulire.
  ganciUscita.forEach((fn) => {
    try {
      fn({ token: keycloak.token });
    } catch (e) {
      // uscire conta piu' che pulire
    }
  });
  // Dopo i ganci, che guardano com'era all'avvio: un'uscita automatica non passa dalla pulizia della rotta /logout.
  vediCome.pulisci();

  // NELL'APP, QUANDO SI ESCE, SI CHIUDE ANCHE LA SESSIONE SUL SERVER.
  //
  // Nell'app si chiede sempre offline_access, e una sessione offline vive 30 giorni lato
  // Keycloak anche dopo che il telefono ha cancellato i suoi token. Senza revoca, il gettone di
  // rinnovo resterebbe valido per un mese: su un telefono perso o passato ad altri, «Esci» non
  // chiuderebbe davvero niente. Si revoca PRIMA di dimenticarlo.
  //
  // TRANNE quando Keycloak ha appena rifiutato la sessione (rinnovo_rifiutato): li' non c'e'
  // niente da revocare. Prima si revocava a ogni uscita, e le uscite automatiche partivano al
  // primo 401 con la sessione buonissima: era la revoca a cancellare davvero i trenta giorni.
  // Adesso un 401 porta prima al rinnovo, e si esce solo se il server rifiuta anche il token
  // appena rinnovato (401_dopo_rinnovo): la sessione sara' anche buona, ma il telefono butta i
  // suoi token comunque, e lasciarla viva vorrebbe dire una sessione orfana di trenta giorni a
  // ogni uscita. Il perche' resta nel resoconto e nella riga del 401 sul server.
  //
  // Niente end-session: e' un redirect verso auth.zoelia.io, cioe' fuori dall'app. La revoca e'
  // una chiamata al backend (/esci), che la chiede a Keycloak; con il corpo form-urlencoded e' una
  // richiesta semplice, senza preflight. keepalive la lascia partire anche se la pagina cambia
  // subito dopo. Il sito non entra qui: la sua scelta resta quella di sempre.
  const daRevocare = PIATTAFORMA_MOBILE && motivo !== 'rinnovo_rifiutato' ? keycloak.refreshToken : null;
  if (daRevocare) {
    try {
      fetch(`${PORTA}/esci`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          refreshToken: daRevocare,
          clientId: keycloakConfig.clientId,
        }),
      }).catch(() => { /* uscire conta piu' che revocare */ });
    } catch (e) {
      // uscire conta piu' che revocare
    }
  }

  // Sito, piu' schede: se nell'archivio ci sono i token di un'ALTRA sessione (una scheda in cui
  // si e' rientrati, mentre questa era rimasta indietro con la sessione vecchia), un'uscita
  // automatica di questa scheda non li tocca. Prima li cancellava, e la scheda buona restava senza
  // token salvati. Un'uscita chiesta dalla persona invece cancella tutto, come sempre.
  const tieniArchivio = !PIATTAFORMA_MOBILE && !eVolontaria(motivo) && archivioDiUnAltraSessione();

  // Clear tokens from memory and storage
  keycloak.token = null;
  keycloak.refreshToken = null;
  keycloak.idToken = null;
  keycloak.tokenParsed = null;
  keycloak.authenticated = false;
  if (!tieniArchivio) clearTokens();

  // Anche lo spazio di lavoro salvato: su un computer condiviso, la persona successiva
  // partirebbe con l'ultimo spazio del collega. Il server lo rifiuterebbe comunque, ma e' un
  // giro inutile e un messaggio in meno da capire. Si tocca la chiave direttamente invece di
  // importare spazioService: quello importa userService, e da qui nascerebbe un ciclo.
  try {
    localStorage.removeItem('zoelia_spazio_attivo');
  } catch (e) {
    // storage negato: pazienza
  }

  // Redirect to login page. Nell'app si sostituisce la voce della cronologia invece di
  // aggiungerne una: il tasto indietro di Android, dopo l'uscita, non deve riportare dentro.
  if (PIATTAFORMA_MOBILE) {
    window.location.replace(destinazione);
  } else {
    window.location.href = destinazione;
  }
}

/**
 * Ensure token is valid, refresh if needed
 *
 * Se il token e' ancora buono ma sta per scadere si rinnova; se il rinnovo non passa per la rete
 * si usa quello che c'e', perche' arriva al server prima di scadere. Se invece e' GIA' scaduto
 * non lo si spedisce piu': prima partiva lo stesso, il server rispondeva 401 e il 401 faceva
 * uscire, con la sessione intatta. Si riprova il rinnovo (1, 2, 4 s) e, se la rete non torna, la
 * chiamata fallisce come errore di rete: la persona vede "rete assente", non il login.
 *
 * @param {{percorso?: string|null}} opzioni percorso: la chiamata per cui serve il token, per il
 *   resoconto dell'uscita se Keycloak rifiuta la sessione.
 * @returns {Promise<string|null>} - Valid token or null
 * @throws un errore con rinnovoSenzaRete (vedi eRinnovoSenzaRete) se il token e' scaduto e la
 *   rete non permette di rinnovarlo
 */
export async function ensureValidToken({ percorso = null } = {}) {
  if (!keycloak.authenticated) return null;

  // Check if token is expired or about to expire
  const tokenExp = keycloak.tokenParsed?.exp;
  const now = Math.floor(Date.now() / 1000);

  if (tokenExp && tokenExp - now < MIN_TOKEN_VALIDITY) {
    let esito = await refreshToken();
    if (esito === 'rete' && !tokenUsabile()) {
      esito = await riprovaRinnovo();
    }
    if (esito === 'rete') {
      // Il token vale ancora per qualche secondo: parte con quello, e il timer riprovera'.
      if (tokenUsabile()) return keycloak.token;
      throw erroreRinnovoSenzaRete();
    }
    if (esito !== 'ok') {
      // Keycloak ha rifiutato: si esce, ma non da una pagina pubblica.
      sessioneFinita('rinnovo_rifiutato', { percorso });
      return null;
    }
  }

  return keycloak.token;
}

/**
 * La lingua del profilo Keycloak, quella da cui si parte su un dispositivo che non ne ha scelta
 * una. La scrive il backend (`PUT /account/sicurezza/lingua`), con il nostro gettone e l'API
 * account di Keycloak, come prima faceva questa funzione da sola.
 * @param {string} locale - e.g., "it", "en"
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updateLocale(locale) {
  if (!keycloak.authenticated) {
    return { success: false, error: 'Not authenticated' };
  }

  try {
    const token = await ensureValidToken();

    // fetch e non apiClient, come changePassword: api.js importa questo file.
    const response = await fetch(`${CONFIG_API_BASE_URL}/account/sicurezza/lingua`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ lingua: locale }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error?.message || 'Failed to update locale' };
    }

    // Update local token data
    if (keycloak.tokenParsed) {
      keycloak.tokenParsed.locale = locale;
    }

    return { success: true };
  } catch (error) {
    console.error('Update locale error:', error);
    return { success: false, error: 'Connection error' };
  }
}

/**
 * Change user password via backend API
 * @param {string} currentPassword - The current password
 * @param {string} newPassword - The new password
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function changePassword(currentPassword, newPassword, codice = null) {
  if (!keycloak.authenticated) {
    return { success: false, error: 'Not authenticated' };
  }

  const API_BASE_URL = CONFIG_API_BASE_URL;

  try {
    const token = await ensureValidToken();
    const response = await fetch(`${API_BASE_URL}/account/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword: currentPassword,
        newPassword: newPassword,
        // Il codice dell'app, solo per chi usa quel fattore: il server lo passa al password
        // grant che verifica la password attuale, e senza quel grant rifiuterebbe sempre.
        ...(codice ? { codice: String(codice).replace(/\D/g, '') } : {}),
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = data.error?.message || data.message || 'Failed to change password';
      return { success: false, error: errorMessage };
    }

    return { success: true };
  } catch (error) {
    console.error('Change password error:', error);
    return { success: false, error: 'Connection error. Please try again.' };
  }
}

/**
 * Get user initials from profile
 * @returns {string}
 */
export function getUserInitials() {
  if (!keycloak.authenticated || !keycloak.tokenParsed) return '??';

  const guardato = vediCome.stato();
  if (guardato) {
    const parti = String(guardato.nome || "").split(/[\s@.]+/).filter(Boolean);
    return `${(parti[0] || '?').charAt(0)}${(parti[1] || '').charAt(0)}`.toUpperCase();
  }

  const firstName = keycloak.tokenParsed.given_name || '';
  const lastName = keycloak.tokenParsed.family_name || '';

  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  } else if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  } else if (keycloak.tokenParsed.preferred_username) {
    return keycloak.tokenParsed.preferred_username.substring(0, 2).toUpperCase();
  }
  return '??';
}

// Export keycloak instance for advanced usage
export { keycloak };

export default {
  init: initKeycloak,
  login,
  getToken,
  getUserProfile,
  isAuthenticated,
  haSessioneSalvata,
  isInitialized,
  waitForInit,
  hasRole,
  logout,
  alUscita,
  ensureValidToken,
  rinnovaDopo401,
  sessioneFinita,
  eRinnovoSenzaRete,
  updateLocale,
  changePassword,
  getUserInitials,
  keycloak,
};
